import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Api from 'services/api';
import AddAppointment from 'components/appointment/AddAppointment';
import CancelModal from 'components/appointment/CancelModal';
import ViewRequestTable from 'views/appointment/view/ViewRequestTable';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import toast from 'react-hot-toast';
import FullPageLoader from "components/common/FullPageLoader";

const AppointmentRequestView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  React.useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const fetchRequestDetails = async () => {
    setLoading(true);
    try {
      const result = await Api.getClientAppointmentRequest(id);
      if (result?.status === 200 && result?.data?.data) {
        const data = result.data.data;
        setItem({
          ...data,
          appointment_details: data.request_details,
          appointment_custom_pricing: data.request_custom_pricing,
          timezone_id: data.request_details?.timezone_id,
        });
      } else {
        toast.error('Appointment request not found');
        navigate('/user/appointment-requests');
      }
    } catch (error) {
      toast.error('Error loading appointment request');
      console.error(error);
      navigate('/user/appointment-requests');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAppointmentForm = () => {
    setShowAppointmentModal(true);
  };

  const handleAppointmentModalClose = () => {
    setShowAppointmentModal(false);
  };

  const handleAppointmentCreated = () => {
    setShowAppointmentModal(false);
    toast.success('Appointment created successfully!');
    navigate('/user/appointment-requests');
  };

  const handleRequestUpdated = () => {
    setShowAppointmentModal(false);
    // toast.success('Appointment request updated successfully!');
    fetchRequestDetails();
  };

  const handleOpenCancel = () => {
    setCancelModal(true);
  };

  const handleCloseCancel = () => {
    setCancelModal(false);
  };

  const handleCancelRequest = async (data) => {
    setLoading(true);
    try {
      const response = await Api.clientCancelRequest(id, data.reason);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Appointment request cancelled successfully!');
        handleCloseCancel();
        fetchRequestDetails();
      } else if (response?.status === 422) {
        toast.error(response.data.message || 'Validation failed');
      } else {
        toast.error(response?.data?.message || 'Failed to cancel appointment request');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error cancelling appointment request');
    } finally {
      setLoading(false);
    }
  };

  // if (loading) {
  //   return (
  //     <div className="content">
  //       <div className="loading-container">
  //         <p>Loading appointment request details...</p>
  //       </div>
  //     </div>
  //   );
  // }

  const ClientTimezone = item?.accounts?.timezone;
  const AppointmentTimezone = item?.appointment_details?.timezone;
  const rawDatetime = item?.datetime;

  // Date: use appointment timezone so late-night appointments don't shift the date
  const formattedDate = AppointmentTimezone?.name
    ? moment.tz(rawDatetime, AppointmentTimezone?.name).format('MMM D, YYYY')
    : moment(rawDatetime).format('MMM D, YYYY');

  // Time: parse stored datetime as appointment timezone and label it
  const formattedClientTime = (() => {
    if (!AppointmentTimezone?.name) return moment(rawDatetime).format('hh:mm A');
    const m = moment.tz(rawDatetime, AppointmentTimezone?.name).tz(ClientTimezone?.name);
    const dynamicAbbr = m.format("z");
    const dynamicOffset = m.format("Z");
  
    const zone = dynamicAbbr ? `(${dynamicAbbr} / GMT${dynamicOffset})` : `(GMT${dynamicOffset})`;
    return `${m.format('hh:mm A')} ${zone}`;
  })();

  return (
    <div className="content">
      {loading && <FullPageLoader />}
      {/* Header with Title */}
      <div className="row_main_head bg-primary-secondaryCard">
        <h2 className="main_heading main_heading1">
          {t("appointmentDetail.viewAppointmentRequestHeading", "View Appointment Request #")} {item?.appid || 'N/A'}
        </h2>
        <div className="mb-3">
          <div className="text-transform-capitalize main_heading main_heading1">
            {t("appointmentDetail.client", "Client")}: {item?.accounts?.user?.name || 'N/A'}
          </div>
        </div>
      </div>

      {/* Date and Time Info */}
      <div className="row_main_head bg-light-secondaryCard mt-3">
        <h2 className="main_heading2 view-page">
          {t("table.date", "Date")} <span className="dateTime">{formattedDate}</span> <span className="dateTime">{formattedClientTime}</span>
        </h2>
        <div className="">
          <div className="text-transform-capitalize main_heading2 d-flex align-items-center">
            <span className="appointmentType viewpage_tags">
              {item?.type || 'N/A'}
            </span>
            <span className="appLanguage viewpage_tags">{item?.language?.name ? item?.language?.name : item?.language_requests?.[0]?.language_name || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card_table view_table_custom">
        <div className="row-primary flex-wrap">
          {/* {!item?.appointment_id && item?.status !== 'cancelled' && item?.status == 'Request' || item?.status == 'New Request' && (
            <button className="main_button  lightGrey" onClick={handleOpenAppointmentForm}>
              Edit Request
            </button>
          )} */}
          
          {!item?.appointment_id && item?.status !== 'cancelled' && (
            <button className="main_button  tomatoRed" onClick={handleOpenCancel}>
              {t("button.cancelRequest", "Cancel Request")}
            </button>
          )}
        </div>
        {/* Details Table */}
        <ViewRequestTable item={item} isRequestView={true} />
      </div>

      {/* AddAppointment Modal */}
      <AddAppointment
        item={item}
        show={showAppointmentModal}
        onClose={handleAppointmentModalClose}
        callback={handleRequestUpdated}
        popupType="edit-request"
      />
      <CancelModal
        isOpen={cancelModal}
        onClose={handleCloseCancel}
        onConfirm={handleCancelRequest}
        appointmentId={item?.appid || item?.id}
        item={item}
        loader={loading}
        heading="Request"
      />
    </div>
  );
};

export default AppointmentRequestView;
